import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faTh } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Attribute } from 'src/app/models/attribute';
import { Rights } from 'src/app/models/enums/rights';
import { IListLayoutAccessibility } from 'src/app/models/interfaces/list-layout-accessibility.interface';
import { IListLayout } from 'src/app/models/interfaces/list-layout.interface';
import { ListLayout } from 'src/app/models/list-layout';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { ListLayoutService } from 'src/app/services/list-layout.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ListLayoutWorkflowsDetailContext } from './list-layout-workflows-detail-context';
import { ListLayoutWorkflowsDetailComponent } from './list-layout-workflows-detail/list-layout-workflows-detail.component';
import { ListLayoutWorkflowsFormContext } from './list-layout-workflows-form-context';
import { ListLayoutsWorkflowsListComponent } from './list-layouts-workflows-list/list-layouts-workflows-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-list-layouts-workflows',
    templateUrl: './list-layouts-workflows.component.html',
    styleUrls: ['./list-layouts-workflows.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ListLayoutsWorkflowsListComponent, DetailHeaderComponent, ListLayoutWorkflowsDetailComponent],
})
export class ListLayoutsWorkflowsComponent extends DefaultManagementComponent<IListLayout, ListLayout, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faListLayout = faTh;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre Attribute.
     */
    public defaultContext: ListLayoutWorkflowsDetailContext = new ListLayoutWorkflowsDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Liste des attributs de la vue courante
     */
    attributes: Attribute[];

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param listLayoutService : Service métier utilisé pour gérer les listlayout.
     */
    constructor(
        protected override notificationService: NotificationService,
        private listLayoutService: ListLayoutService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService,
        private attributesService: AttributeDefinitionsService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ListLayoutCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ListLayoutEdit);

        this.loadAttributes();
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param listLayout Mise en page selectionnée.
     */
    public onListLayoutSelected(listLayout: ListLayout) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadListLayout(listLayout.id).subscribe(
            (x) => {
                this.refresh(ListLayout.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher la disposition." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param listLayout Mise en page selectionnée.
     */
    public onListLayoutDisplayed(listLayout: ListLayout) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadListLayout(listLayout.id).subscribe(
            (x) => {
                this.refresh(ListLayout.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher la mise en page." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param listLayout Mise en page selectionnée.
     */
    public onListLayoutEdited(listLayout: ListLayout) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadListLayout(listLayout.id).subscribe(
            (x) => {
                this.refresh(ListLayout.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer la mise en page." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onListLayoutCreated() {
        this.displayForm(ListLayout.createFormCopy(new ListLayout()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param listLayout Mise en page selectionnée.
     */
    public onListLayoutDuplicated(listLayout: ListLayout) {
        let duplicatedListLayout = ListLayout.createFormCopy(listLayout);
        duplicatedListLayout.id = null;
        this.displayForm(duplicatedListLayout, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param listLayout : Valeur de la mise en page retournée par le formulaire.
     */
    public onListLayoutUpdated(listLayout: ListLayout): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.listLayoutService.update(this.context.editedItemId, listLayout).subscribe(
                (x) => {
                    this.refreshWithNotification(ListLayout.createFormCopy(x), 'Mise en page modifiée', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de modifier la mise en page.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.listLayoutService.add(listLayout).subscribe(
                (x) => {
                    this.refreshWithNotification(ListLayout.createFormCopy(x), 'Mise en page créée', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer la mise en page.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /** Appelé lorsque l'accessibilité à la mise en page a été modifiée */
    public onAccessibilitiesUpdated(accessibilities: IListLayoutAccessibility[]): void {
        this.spinner.show('main-spinner');
        this.listLayoutService.updateAccessibilities(this.context.editedItemId, accessibilities).subscribe(
            (x) => {
                this.refreshWithNotification(this.context.formContext.item, 'Mises en page par défaut modifiées', FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de modifier les mises en page par défaut.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param listLayout Mise en page selectionnée.
     */
    public onListLayoutDeleted(listLayout: ListLayout) {
        this.spinner.show('main-spinner');
        // Suppression effective du listLayout.
        this.listLayoutService.delete(listLayout.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Mise en page supprimée');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer la mise en page.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onListLayoutExported() {
        this.createContext();
        this.itemsList.export('listLayout-workflows.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onListLayoutImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onListLayoutHelped() {
        this.itemsList.displayHelpDialog('listLayoutsWorkflows');
    }

    /**
     * Charge les données d'un listlayout.
     * @param listLayoutId : Id du listlayout à charger.
     */
    private loadListLayout(listLayoutId: number): Observable<ListLayout> {
        return this.listLayoutService.get(listLayoutId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param listLayout : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(listLayout: IListLayout): ListLayoutWorkflowsDetailContext {
        let context = this.createContext();

        context.formContext.item = listLayout;
        context.editedItemId = listLayout.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ListLayoutWorkflowsDetailContext {
        let context = new ListLayoutWorkflowsDetailContext();
        (context.formContext as ListLayoutWorkflowsFormContext).attributes = this.attributes;
        return context;
    }

    private loadAttributes() {
        this.spinner.show('main-spinner');

        this.attributesService.List(null, null, null, null).subscribe({
            next: (attributes) => {
                this.attributes = attributes.value;
                this.spinner.hide('main-spinner');
            },
            error: (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des attributs.' + error.message);
                this.spinner.hide('main-spinner');
            },
        });
    }
}
